import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import {
  copyIcon,
  LinkIcon,
  playerStopFilledIcon,
  share,
  shareIOS,
  shareWindows,
} from "@excalidraw/excalidraw/components/icons";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { useCopyStatus } from "@excalidraw/excalidraw/hooks/useCopiedIndicator";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { KEYS } from "@excalidraw/common";
import { useEffect, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { atom, useAtom, useAtomValue } from "../app-jotai";
import { activeRoomLinkAtom } from "../collab/Collab";
import { libraryApi, type Collaborator } from "../data/libraryApi";
import { useAuth } from "../auth/AuthContext";

import "./ShareDialog.scss";
import { QRCode } from "./QRCode";

import type { CollabAPI } from "../collab/Collab";

type ShareDialogType = "share" | "collaborationOnly";

export const shareDialogStateAtom = atom<
  { isOpen: false } | { isOpen: true; type: ShareDialogType }
>({ isOpen: false });

const getShareIcon = () => {
  const navigator = window.navigator as any;
  const isAppleBrowser = /Apple/.test(navigator.vendor);
  const isWindowsBrowser = navigator.appVersion.indexOf("Win") !== -1;

  if (isAppleBrowser) {
    return shareIOS;
  } else if (isWindowsBrowser) {
    return shareWindows;
  }

  return share;
};

export type ShareDialogProps = {
  collabAPI: CollabAPI | null;
  handleClose: () => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  activeCanvasId: string | null;
  type: ShareDialogType;
};

const ActiveRoomDialog = ({
  collabAPI,
  activeRoomLink,
  handleClose,
}: {
  collabAPI: CollabAPI;
  activeRoomLink: string;
  handleClose: () => void;
}) => {
  const { t } = useI18n();
  const [, setJustCopied] = useState(false);
  const timerRef = useRef<number>(0);
  const ref = useRef<HTMLInputElement>(null);
  const isShareSupported = "share" in navigator;
  const { onCopy, copyStatus } = useCopyStatus();

  const copyRoomLink = async () => {
    try {
      await copyTextToSystemClipboard(activeRoomLink);
    } catch (e) {
      collabAPI.setCollabError(t("errors.copyToSystemClipboardFailed"));
    }

    setJustCopied(true);

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      setJustCopied(false);
    }, 3000);

    ref.current?.select();
  };

  const shareRoomLink = async () => {
    try {
      await navigator.share({
        title: t("roomDialog.shareTitle"),
        text: t("roomDialog.shareTitle"),
        url: activeRoomLink,
      });
    } catch (error: any) {
      // Just ignore.
    }
  };

  return (
    <>
      <h3 className="ShareDialog__active__header">
        {t("labels.liveCollaboration").replace(/\./g, "")}
      </h3>
      <TextField
        defaultValue={collabAPI.getUsername()}
        placeholder="Your name"
        label="Your name"
        onChange={collabAPI.setUsername}
        onKeyDown={(event) => event.key === KEYS.ENTER && handleClose()}
      />
      <div className="ShareDialog__active__linkRow">
        <TextField
          ref={ref}
          label="Link"
          readonly
          fullWidth
          value={activeRoomLink}
        />
        {isShareSupported && (
          <FilledButton
            size="large"
            variant="icon"
            label="Share"
            icon={getShareIcon()}
            className="ShareDialog__active__share"
            onClick={shareRoomLink}
          />
        )}
        <FilledButton
          size="large"
          label={t("buttons.copyLink")}
          icon={copyIcon}
          status={copyStatus}
          onClick={() => {
            copyRoomLink();
            onCopy();
          }}
        />
      </div>
      <QRCode value={activeRoomLink} />
      <div className="ShareDialog__active__description">
        <p>
          <span
            role="img"
            aria-hidden="true"
            className="ShareDialog__active__description__emoji"
          >
            🔒{" "}
          </span>
          {t("roomDialog.desc_privacy")}
        </p>
        <p>{t("roomDialog.desc_exitSession")}</p>
      </div>

      <div className="ShareDialog__active__actions">
        <FilledButton
          size="large"
          variant="outlined"
          color="danger"
          label={t("roomDialog.button_stopSession")}
          icon={playerStopFilledIcon}
          onClick={() => {
            trackEvent("share", "room closed");
            collabAPI.stopCollaboration();
            if (!collabAPI.isCollaborating()) {
              handleClose();
            }
          }}
        />
      </div>
    </>
  );
};

const SelfHostedShareSection = ({
  excalidrawAPI,
  activeCanvasId,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  activeCanvasId: string | null;
}) => {
  const { t } = useI18n();
  const { onCopy, copyStatus } = useCopyStatus();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pre-fill the existing, permanent link for this canvas (if any) so the same
  // URL shows up every time instead of minting a new one.
  useEffect(() => {
    let cancelled = false;
    setShareUrl(null);
    setToken(null);
    setCanEdit(false);
    if (!activeCanvasId) {
      return;
    }
    libraryApi
      .getShareForCanvas(activeCanvasId)
      .then((res) => {
        if (!cancelled) {
          setShareUrl(`${window.location.origin}/share/${res.token}`);
          setToken(res.token);
          setCanEdit(res.can_edit === 1);
        }
      })
      .catch(() => {
        /* no share yet — leave the create button */
      });
    return () => {
      cancelled = true;
    };
  }, [activeCanvasId]);

  // Load the canvas description so the textarea reflects what's stored.
  useEffect(() => {
    let cancelled = false;
    if (!activeCanvasId) {
      setDescription("");
      return;
    }
    libraryApi
      .getCanvas(activeCanvasId)
      .then((meta) => {
        if (!cancelled) {
          setDescription(meta.description ?? "");
        }
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [activeCanvasId]);

  // Persist the description to the canvas, debounced while the user types.
  const onDescriptionChange = (value: string) => {
    setDescription(value);
    if (!activeCanvasId) {
      return;
    }
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    saveTimer.current = setTimeout(() => {
      libraryApi
        .updateCanvas(activeCanvasId, { description: value || null })
        .catch(() => {
          /* ignore — will retry on next edit */
        });
    }, 700);
  };

  const createLink = async () => {
    if (!excalidrawAPI || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const appState = excalidrawAPI.getAppState();
      const scene = {
        type: "excalidraw",
        version: 2,
        source: "self-hosted",
        elements: excalidrawAPI.getSceneElements(),
        appState: { viewBackgroundColor: appState.viewBackgroundColor },
        files: excalidrawAPI.getFiles(),
      };
      let name: string | null = null;
      if (activeCanvasId) {
        try {
          const meta = await libraryApi.getCanvas(activeCanvasId);
          name = meta.name;
        } catch {
          /* ignore — share without a name */
        }
      }
      // Flush any pending debounced description save so the snapshot is fresh.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      const res = await libraryApi.createShare({
        scene,
        name,
        canvas_id: activeCanvasId,
        description: description || null,
        can_edit: canEdit,
      });
      const url = `${window.location.origin}/share/${res.token}`;
      setShareUrl(url);
      setToken(res.token);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Toggle guest editing. If a link exists, persist immediately; otherwise just
  // remember the choice so it's applied when the link is created.
  const toggleCanEdit = async (next: boolean) => {
    setCanEdit(next);
    if (token) {
      try {
        await libraryApi.updateShare(token, { can_edit: next });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setCanEdit(!next); // revert on failure
      }
    }
  };

  const copyLink = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await copyTextToSystemClipboard(shareUrl);
      onCopy();
    } catch {
      // insecure context — the input is selected so the user can copy manually
    }
  };

  return (
    <>
      <div className="ShareDialog__picker__header">
        {t("shareLink.dialogTitle")}
      </div>
      <div className="ShareDialog__picker__description">
        {t("shareLink.hint")}
      </div>

      <label className="ShareDialog__descriptionField">
        <span className="ShareDialog__descriptionField__label">
          {t("shareLink.descriptionLabel")}
        </span>
        <textarea
          className="ShareDialog__descriptionField__input"
          rows={2}
          maxLength={300}
          placeholder={t("shareLink.descriptionPlaceholder")}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
        />
      </label>

      {shareUrl ? (
        <div className="ShareDialog__active__linkRow">
          <TextField
            ref={inputRef}
            label="Link"
            readonly
            fullWidth
            value={shareUrl}
          />
          <FilledButton
            size="large"
            label={t("shareLink.copy")}
            icon={copyIcon}
            status={copyStatus}
            onClick={copyLink}
          />
        </div>
      ) : (
        <div className="ShareDialog__picker__button">
          <FilledButton
            size="large"
            label={busy ? "…" : t("shareLink.createLink")}
            icon={LinkIcon}
            onClick={createLink}
          />
        </div>
      )}

      <label className="ShareDialog__toggle">
        <input
          type="checkbox"
          checked={canEdit}
          onChange={(e) => toggleCanEdit(e.target.checked)}
        />
        <span className="ShareDialog__toggle__text">
          <span className="ShareDialog__toggle__label">
            {t("collab.allowGuestEdit")}
          </span>
          <span className="ShareDialog__toggle__hint">
            {canEdit
              ? t("collab.allowGuestEditOn")
              : t("collab.allowGuestEditOff")}
          </span>
        </span>
      </label>

      {error && (
        <div className="ShareDialog__picker__description ShareDialog__error">
          {error}
        </div>
      )}
    </>
  );
};

// Account-to-account shared editing: grant other registered users edit access
// to this board. They see it under "Shared with me" and can co-edit live.
const CollaboratorsSection = ({
  activeCanvasId,
}: {
  activeCanvasId: string | null;
}) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [list, setList] = useState<Collaborator[]>([]);
  const [ownerUsername, setOwnerUsername] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<
    { id: string; username: string }[]
  >([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = () => {
    if (!activeCanvasId) {
      return;
    }
    libraryApi
      .listCollaborators(activeCanvasId)
      .then((res) => {
        setOwnerUsername(res.owner_username);
        setList(res.collaborators);
      })
      .catch(() => {
        /* offline / not accessible — leave empty */
      });
  };

  useEffect(() => {
    setError(null);
    setQuery("");
    setSuggestions([]);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCanvasId]);

  // Look up candidate users (debounced). Empty query → first few users; as the
  // user types we hit the DB so the picker never loads the whole user table.
  const runSearch = (q: string) => {
    libraryApi
      .searchUsers(q)
      .then(setSuggestions)
      .catch(() => setSuggestions([]));
  };

  const onQueryChange = (value: string) => {
    setQuery(value);
    setOpen(true);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(() => runSearch(value.trim()), 220);
  };

  // Only the owner manages access. (For a board shared WITH me, owner differs.)
  const isOwner = ownerUsername === null || ownerUsername === user.username;

  const add = async (name: string) => {
    const username = name.trim();
    if (!activeCanvasId || !username || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await libraryApi.addCollaborator(activeCanvasId, username);
      setQuery("");
      setSuggestions([]);
      setOpen(false);
      reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        msg.includes("404")
          ? t("collab.userNotFound")
          : msg.includes("400")
          ? t("collab.cannotAddSelf")
          : msg,
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string) => {
    if (!activeCanvasId) {
      return;
    }
    try {
      await libraryApi.removeCollaborator(activeCanvasId, userId);
      reload();
    } catch {
      /* ignore — reload keeps UI consistent */
    }
  };

  if (!activeCanvasId || !isOwner) {
    return null;
  }

  // Hide users that are already collaborators (or me) from the picker.
  const existing = new Set(list.map((c) => c.username));
  const visibleSuggestions = suggestions.filter(
    (s) => !existing.has(s.username) && s.username !== user.username,
  );

  return (
    <>
      <div className="ShareDialog__separator">
        <span>{t("collab.collaboratorsTitle")}</span>
      </div>
      <div className="ShareDialog__picker__description">
        {t("collab.collaboratorsHint")}
      </div>

      <div className="ShareDialog__collab-add">
        <div className="ShareDialog__collab-add__row">
          <div className="ShareDialog__collab-add__field">
            <input
              type="text"
              className="ShareDialog__collab-add__input"
              value={query}
              placeholder={t("collab.searchPlaceholder")}
              autoComplete="off"
              onChange={(e) => onQueryChange(e.target.value)}
              onFocus={() => {
                setOpen(true);
                runSearch(query.trim());
              }}
              onBlur={() => {
                blurTimer.current = setTimeout(() => setOpen(false), 150);
              }}
              onKeyDown={(event) => {
                if (event.key === KEYS.ENTER) {
                  add(query);
                } else if (event.key === KEYS.ESCAPE) {
                  setOpen(false);
                }
              }}
            />
            {open && visibleSuggestions.length > 0 && (
              <ul className="ShareDialog__user-suggestions">
                {visibleSuggestions.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      className="ShareDialog__user-suggestions__item"
                      // mousedown fires before the input's blur, so the click
                      // isn't swallowed by the dropdown closing.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (blurTimer.current) {
                          clearTimeout(blurTimer.current);
                        }
                        add(u.username);
                      }}
                    >
                      {u.username}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <FilledButton
            size="large"
            label={busy ? "…" : t("collab.add")}
            onClick={() => add(query)}
          />
        </div>
      </div>

      {list.length > 0 && (
        <ul className="ShareDialog__collaborators">
          {list.map((c) => (
            <li key={c.user_id} className="ShareDialog__collaborators__item">
              <span className="ShareDialog__collaborators__name">
                {c.username}
              </span>
              <button
                type="button"
                className="ShareDialog__collaborators__remove"
                title={t("collab.remove")}
                aria-label={t("collab.remove")}
                onClick={() => remove(c.user_id)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div className="ShareDialog__picker__description ShareDialog__error">
          {error}
        </div>
      )}
    </>
  );
};

const ShareDialogPicker = (props: ShareDialogProps) => {
  // PizzarWeb: the native Excalidraw "start session" (external Firebase/socket
  // room collab) was removed. Sharing is fully self-hosted: a public link
  // (read-only, or guest-editable per-canvas) + account collaborators.
  return (
    <>
      <CollaboratorsSection activeCanvasId={props.activeCanvasId} />
      <SelfHostedShareSection
        excalidrawAPI={props.excalidrawAPI}
        activeCanvasId={props.activeCanvasId}
      />
    </>
  );
};

const ShareDialogInner = (props: ShareDialogProps) => {
  const activeRoomLink = useAtomValue(activeRoomLinkAtom);

  return (
    <Dialog size="small" onCloseRequest={props.handleClose} title={false}>
      <div className="ShareDialog">
        {props.collabAPI && activeRoomLink ? (
          <ActiveRoomDialog
            collabAPI={props.collabAPI}
            activeRoomLink={activeRoomLink}
            handleClose={props.handleClose}
          />
        ) : (
          <ShareDialogPicker {...props} />
        )}
      </div>
    </Dialog>
  );
};

export const ShareDialog = (props: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  activeCanvasId: string | null;
}) => {
  const [shareDialogState, setShareDialogState] = useAtom(shareDialogStateAtom);

  const { openDialog } = useUIAppState();

  useEffect(() => {
    if (openDialog) {
      setShareDialogState({ isOpen: false });
    }
  }, [openDialog, setShareDialogState]);

  if (!shareDialogState.isOpen) {
    return null;
  }

  return (
    <ShareDialogInner
      handleClose={() => setShareDialogState({ isOpen: false })}
      collabAPI={props.collabAPI}
      excalidrawAPI={props.excalidrawAPI}
      activeCanvasId={props.activeCanvasId}
      type={shareDialogState.type}
    />
  );
};
